/**
 * Roster 3: the factory EntityManager falls through to, hitbox sizes, the
 * biome herd / water spawn rules the Spawner asks for, and the one-off
 * population of ocean monuments and woodland mansions. DOM-free, no state of
 * its own: everything is a function of what the caller passes in.
 */
import { IS_SOLID, IS_WATERY } from '../../blocks';
import type { MobType } from '../../game/types';
import type { Mob } from '../mob';
import { Goat, Mooshroom, Ocelot, PolarBear, Rabbit, SnowGolem, Turtle, rabbitCoat } from './animals';
import { Evoker, EvokerFangs, PiglinBrute, Ravager, Shulker, ShulkerBullet, Vex, Zoglin } from './hostile';
import { Axolotl, Dolphin, Guardian } from './water';

export { snowGolemPattern } from './animals';

type R = () => number;

export function createMobs3Mob(type: MobType, id: number, x: number, y: number, z: number, r: R): Mob | null {
  switch (type) {
    case 'rabbit': return new Rabbit(id, x, y, z, r);
    case 'goat': return new Goat(id, x, y, z, r);
    case 'turtle': return new Turtle(id, x, y, z, r);
    case 'mooshroom': return new Mooshroom(id, x, y, z, r);
    case 'polar_bear': return new PolarBear(id, x, y, z, r);
    case 'ocelot': return new Ocelot(id, x, y, z, r);
    case 'snow_golem': return new SnowGolem(id, x, y, z, r);
    case 'axolotl': return new Axolotl(id, x, y, z, r);
    case 'dolphin': return new Dolphin(id, x, y, z, r);
    case 'guardian': return new Guardian(id, x, y, z, r);
    case 'elder_guardian': return new Guardian(id, x, y, z, r, true);
    case 'shulker': return new Shulker(id, x, y, z, r);
    case 'shulker_bullet': return new ShulkerBullet(id, x, y, z, r);
    case 'evoker': return new Evoker(id, x, y, z, r);
    case 'evoker_fangs': return new EvokerFangs(id, x, y, z, r);
    case 'vex': return new Vex(id, x, y, z, r);
    case 'ravager': return new Ravager(id, x, y, z, r);
    case 'zoglin': return new Zoglin(id, x, y, z, r);
    case 'piglin_brute': return new PiglinBrute(id, x, y, z, r);
    default: return null;
  }
}

/** Hitbox [width, height] per type (vanilla EntityType sizes). */
export const MOBS3_SIZE = {
  rabbit: [0.4, 0.5], goat: [0.9, 1.3], turtle: [1.2, 0.4], mooshroom: [0.9, 1.4], polar_bear: [1.4, 1.4], ocelot: [0.6, 0.7],
  snow_golem: [0.7, 1.9], axolotl: [0.75, 0.42], dolphin: [0.9, 0.6], guardian: [0.85, 0.85], elder_guardian: [1.9975, 1.9975],
  shulker: [1, 1], shulker_bullet: [0.3125, 0.3125], evoker: [0.6, 1.95], evoker_fangs: [0.5, 0.8], vex: [0.4, 0.8],
  ravager: [1.95, 2.2], zoglin: [1.3965, 1.4], piglin_brute: [0.6, 1.95],
} satisfies Partial<Record<MobType, [number, number]>>;

// ---------------------------------------------------------------- spawning

export type HerdFloor = 'grass' | 'sand' | 'snow' | 'mycelium' | 'stone';
export interface Herd3 { type: MobType; count: number; floor: HerdFloor }

/**
 * The herd a chunk gets instead of farm animals, from its (lower-case) biome
 * name and two rolls in [0, 1): vanilla's biome spawn lists, as the share of
 * herds each mob takes.
 */
export function mobs3Herd(roll: number, sizeRoll: number, biome: string): Herd3 | null {
  const n = (lo: number, hi: number): number => lo + Math.floor(sizeRoll * (hi - lo + 1));
  if (biome.includes('mushroom')) return { type: 'mooshroom', count: n(4, 8), floor: 'mycelium' };
  if (/jagged peaks|frozen peaks|snowy slopes/.test(biome)) return roll < 0.5 ? { type: 'goat', count: n(1, 3), floor: 'stone' } : roll < 0.7 && biome.includes('slopes') ? { type: 'rabbit', count: n(2, 3), floor: 'snow' } : null;
  if (biome.includes('desert')) return roll < 0.4 ? { type: 'rabbit', count: n(2, 3), floor: 'sand' } : null;
  if (/snowy plains|ice spikes|frozen river/.test(biome)) return roll < 0.12 ? { type: 'polar_bear', count: n(1, 2), floor: 'snow' } : roll < 0.4 ? { type: 'rabbit', count: n(2, 3), floor: 'snow' } : null;
  if (/snowy taiga|grove/.test(biome)) return roll > 0.75 ? { type: 'rabbit', count: n(2, 3), floor: 'snow' } : null;
  if (/meadow|flower forest|cherry/.test(biome) || biome.includes('taiga')) return roll > 0.8 ? { type: 'rabbit', count: n(2, 3), floor: 'grass' } : null;
  if (biome === 'beach') return roll < 0.2 ? { type: 'turtle', count: n(2, 5), floor: 'sand' } : null;
  if (biome.includes('jungle')) return roll < 0.3 ? { type: 'ocelot', count: n(1, 3), floor: 'grass' } : null;
  return null;
}

/** Dolphins take a share of the ocean ambient spawns (not in frozen or cold water); `roll` is the ambient type roll. */
export function mobs3Ocean(roll: number, biome: string): MobType | null {
  return roll > 0.9 && biome.includes('ocean') && !/frozen|cold/.test(biome) ? 'dolphin' : null;
}

/** A freshly spawned herd member gets its biome's look (rabbit coats). */
export function dressHerdMob(m: unknown, biome: string, r: R): void {
  if (m instanceof Rabbit) { m.coat = rabbitCoat(biome, r()); m.color = m.coat; }
  if (m instanceof Turtle || m instanceof Rabbit || m instanceof Goat || m instanceof Mooshroom || m instanceof PolarBear) { if (r() < 0.1) m.setBaby(true); }
}

// -------------------------------------------------------------- structures

export interface StructureMobDeps {
  world: { getBlock(x: number, y: number, z: number): number };
  spawn(type: MobType, x: number, y: number, z: number): Mob | null;
  mobs(): readonly Mob[];
  flag: { has(key: string): boolean; set(key: string): void };
  rnd: R;
  /** Every chunk within r blocks of (x, z) is loaded. */
  ready(x: number, z: number, r: number): boolean;
  tick: number;
}

/** Feet position with `need` free cells (water or air) over a solid floor (air only), in a box around the centre. */
function findCell(d: StructureMobDeps, cx: number, cy: number, cz: number, rx: number, y0: number, y1: number, rz: number, wet: boolean): [number, number, number] | null {
  const w = d.world;
  for (let i = 0; i < 40; i++) {
    const x = Math.floor(cx + (d.rnd() * 2 - 1) * rx), z = Math.floor(cz + (d.rnd() * 2 - 1) * rz), y = cy + y0 + Math.floor(d.rnd() * (y1 - y0 + 1));
    if (wet) {
      if (IS_WATERY[w.getBlock(x, y, z)] === 1 && IS_WATERY[w.getBlock(x, y + 1, z)] === 1 && IS_WATERY[w.getBlock(x, y - 1, z)] === 1) return [x + 0.5, y, z + 0.5];
    } else if (IS_SOLID[w.getBlock(x, y - 1, z)] === 1 && w.getBlock(x, y, z) === 0 && w.getBlock(x, y + 1, z) === 0) return [x + 0.5, y, z + 0.5];
  }
  return null;
}

export const MONUMENT_GUARDIANS = 6;

/**
 * Ocean monuments get their three elder guardians once and are kept stocked
 * with guardians (vanilla spawns them in the monument's box for ever);
 * woodland mansions get two evokers and four vindicators once. Called for
 * each nearby structure every 100 ticks or so. Returns the mobs it spawned.
 */
export function populateStructureMobs(d: StructureMobDeps, s: { name: string; x: number; y: number; z: number }): number {
  const sx = Math.round(s.x), sz = Math.round(s.z);
  let n = 0;
  if (s.name === 'Ocean Monument') {
    if (!d.ready(sx, sz, 24)) return 0;
    const key = `monument:${sx},${sz}`;
    if (!d.flag.has(key)) {
      d.flag.set(key);
      for (let i = 0; i < 3; i++) {
        const c = findCell(d, sx, s.y, sz, 14, 2, 6, 14, true);
        const m = c && d.spawn('elder_guardian', c[0], c[1], c[2]);
        if (m) { m.persistent = true; n++; }
      }
    }
    if (d.tick % 600 < 100) {
      let have = 0;
      for (const m of d.mobs()) if (m.type === 'guardian' && m.alive && (m.x - sx) ** 2 + (m.z - sz) ** 2 < 40 * 40) have++;
      for (let i = have; i < MONUMENT_GUARDIANS && i < have + 2; i++) {
        const c = findCell(d, sx, s.y, sz, 18, 1, 12, 18, true);
        if (c && d.spawn('guardian', c[0], c[1], c[2])) n++;
      }
    }
  } else if (s.name === 'Woodland Mansion') {
    const key = `mansion:${sx},${sz}`;
    if (d.flag.has(key) || !d.ready(sx, sz, 20)) return 0;
    d.flag.set(key);
    for (let i = 0; i < 6; i++) {
      const c = findCell(d, sx, s.y, sz, 9, 1, 7, 8, false);
      const m = c && d.spawn(i < 2 ? 'evoker' : 'vindicator', c[0], c[1], c[2]);
      if (m) { m.persistent = true; n++; }
    }
  }
  return n;
}

/** End cities: a shulker on some of the purpur floors (server/actions/mobs3.ts calls this once per city). */
export function populateEndCity(d: StructureMobDeps, c: { cx: number; cz: number; y: number; ship: boolean }, purpurIds: ReadonlySet<number>): number {
  const w = d.world;
  let n = 0;
  for (let i = 0; i < 400 && n < (c.ship ? 14 : 10); i++) {
    const x = c.cx - 6 + Math.floor(d.rnd() * 43), z = c.cz - 6 + Math.floor(d.rnd() * 13), y = c.y + 1 + Math.floor(d.rnd() * 40);
    if (!purpurIds.has(w.getBlock(x, y - 1, z)) || w.getBlock(x, y, z) !== 0) continue;
    if (d.mobs().some((m) => m.type === 'shulker' && Math.floor(m.x) === x && Math.floor(m.z) === z && Math.floor(m.y) === y)) continue;
    if (d.spawn('shulker', x + 0.5, y, z + 0.5)) n++;
  }
  return n;
}

export { Axolotl, Dolphin, Evoker, EvokerFangs, Goat, Guardian, Mooshroom, Ocelot, PiglinBrute, PolarBear, Rabbit, Ravager, Shulker, ShulkerBullet, SnowGolem, Turtle, Vex, Zoglin };
